import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { AngularMaterialModule } from 'src/app/material.module';
// Import directly from value-field.module (not the @lib/modules barrel) — that barrel also
// re-exports table.module, whose table.component.ts has a pre-existing circular import via the
// @lib/modules/table/components barrel that breaks Karma's webpack bundling (unrelated to this change).
import { ValueFieldModule } from '@lib/modules/value-field/value-field.module';
import { BaseComponent } from '@lib/components';
import { ApiResponseError, ICustomer } from '@lib/interfaces';
import { ETransferToAgentErrorDetail, ISenderInfo, ITransferToAgentRequest } from '@lib/interfaces/super-agent-transfer.interface';
import { AccountsService, SidePanelService } from '@lib/services';
import { TransferResultComponent } from '../transfer-result/transfer-result.component';

const ERROR_DETAIL_MESSAGES: Record<string, string> = {
    [ETransferToAgentErrorDetail.InitiatorNotSuperAgent]: 'You are not authorized to top up other agents.',
    [ETransferToAgentErrorDetail.RecipientMustBeAgent]: 'This flow is for agents only. You can top-up for customers using the normal Top-Up flow.',
    [ETransferToAgentErrorDetail.RecipientNotActive]: 'This agent is not active and cannot receive a top-up.',
    [ETransferToAgentErrorDetail.SameAccountNotAllowed]: 'You cannot top up your own wallet using this flow.',
};

@Component({
    selector: 'app-enter-passcode',
    standalone: true,
    templateUrl: './enter-passcode.component.html',
    styleUrls: ['./enter-passcode.component.scss'],
    imports: [AngularMaterialModule, ValueFieldModule],
})
export class EnterPasscodeComponent extends BaseComponent {
    // NEOIQ-2427: passcode is collected here — before this, there's no otp_id/challenge from the
    // backend at all (see confirm-transfer.component.ts — that screen no longer calls the API).
    // This is the ONLY transferToAgent call in the flow. Confirmed by the backend team: the
    // passcode is sent as the `otp_auth` header (accounts.service.ts), not a body field.
    payload: ITransferToAgentRequest;
    recipientInfo: ICustomer;
    senderInfo: ISenderInfo;

    protected passcode = '';
    protected loading = false;

    constructor(private readonly dialogRef: MatDialogRef<EnterPasscodeComponent>, private readonly sidePanel: SidePanelService, private readonly accountsService: AccountsService, private readonly snackbar: MatSnackBar) {
        super();
    }

    // Same "mask all but last 4" convention as MaskStringPipe (@lib/pipes) uses elsewhere.
    protected get maskedSenderMobile(): string {
        const mobile = this.senderInfo?.mobile ?? '';
        return mobile.length > 4 ? `****${mobile.slice(-4)}` : mobile;
    }

    protected setPasscode(value: string): void {
        this.passcode = value;
    }

    protected submitPasscode(): void {
        if (this.passcode.length < 6) return;

        const idempotencyKey = crypto.randomUUID();

        this.loading = true;
        this.addSubscription(
            this.accountsService
                .transferToAgent(this.payload, idempotencyKey, this.passcode)
                .pipe(finalize(() => (this.loading = false)))
                .subscribe({
                    next: (transaction) => {
                        this.dialogRef.close();
                        const dialogRef = this.sidePanel.open(TransferResultComponent);
                        dialogRef.componentInstance.transaction = transaction;
                        dialogRef.componentInstance.recipientInfo = this.recipientInfo;
                        dialogRef.componentInstance.senderInfo = this.senderInfo;
                        dialogRef.componentInstance.isMoneySent = true;
                    },
                    error: (error: ApiResponseError) => this.handleError(error),
                }),
        );
    }

    private handleError(error: ApiResponseError): void {
        this.passcode = '';

        const detail = error.error?.detail ?? '';
        const message = ERROR_DETAIL_MESSAGES[detail];
        if (message) {
            this.createApiErrorSnackBar(this.snackbar, new Error(message));
            return;
        }

        // A 401 with no recognized `detail` is treated as "wrong passcode" — the most likely
        // reason this specific call would come back unauthorized, since the initiator's own
        // eligibility (super-agent status etc.) is already covered by the detail codes above.
        if (error.status === 401) {
            this.createApiErrorSnackBar(this.snackbar, new Error('Incorrect passcode. Please try again.'));
            return;
        }

        this.createApiErrorSnackBar(this.snackbar, error);
    }

    protected cancel(): void {
        this.dialogRef.close();
    }
}
